import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from './Layout';
import { useOrganization } from '../context/OrganizationContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import Cookies from 'js-cookie';
import {
  Activity,
  AlertTriangle,
  Clock3,
  Database,
  FileUp,
  Radio,
  Shield,
  ShieldCheck,
  Sparkles
} from 'lucide-react';

const Dashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();

  const [monthlyData, setMonthlyData] = useState([]);
  const [severityData, setSeverityData] = useState([]);
  const [anomalies, setAnomalies] = useState(0);
  const [highSeverity, setHighSeverity] = useState(0);
  const [mediumSeverity, setMediumSeverity] = useState(0);
  const [lowSeverity, setLowSeverity] = useState(0);
  const [recentThreats, setRecentThreats] = useState([]);

  const [summary, setSummary] = useState(null);
  const [incidentSummary, setIncidentSummary] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [topThreats, setTopThreats] = useState([]);
  const [attackGraph, setAttackGraph] = useState({ nodes: [], edges: [] });
  const [collectorSummary, setCollectorSummary] = useState(null);

  // Platform-wide state is intentionally not loaded in the customer portal.
  // These placeholders keep legacy hidden panels inert until tenant-owned
  // equivalents exist.
  const uploadAudit = [];
  const policy = null;

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const getMonthOrder = (month) => {
    const monthOrder = {
      Apr: 0,
      May: 1,
      Jun: 2,
      Jul: 3,
      Aug: 4,
      Sep: 5,
      Oct: 6,
      Nov: 7,
      Dec: 8,
      Jan: 9,
      Feb: 10,
      Mar: 11
    };

    return monthOrder[month] !== undefined ? monthOrder[month] : 999;
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(false);

        const token = Cookies.get('token');
        const userId = Cookies.get('guid');

        if (!token || !userId) {
          console.error('No authentication token or user ID found');
          navigate('/');
          return;
        }

        const baseUrl = process.env.REACT_APP_CYBEDEFENDER_AI_URL;

        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        };

        const payload = JSON.stringify({ userId });

        const dashboardResponse = await fetch(
          `${baseUrl}/cybedefender/Dashboard`,
          {
            method: 'POST',
            headers,
            body: payload
          }
        );

        if (!dashboardResponse.ok) {
          throw new Error(`Dashboard request failed: ${dashboardResponse.status}`);
        }

        const dashboardData = await dashboardResponse.json();

        const monthData = Object.keys(dashboardData.monthlyData || {})
          .map((month) => ({
            name: month,
            value: dashboardData.monthlyData[month],
            order: getMonthOrder(month)
          }))
          .sort((a, b) => a.order - b.order);

        setMonthlyData(monthData);
        setAnomalies(dashboardData.Anomalies || 0);
        setHighSeverity(dashboardData.High || 0);
        setMediumSeverity(dashboardData.Medium || 0);
        setLowSeverity(dashboardData.Low || 0);

        const totalAnomalies =
          (dashboardData.High || 0) +
          (dashboardData.Medium || 0) +
          (dashboardData.Low || 0);

        const highPercentage = totalAnomalies
          ? Math.round(((dashboardData.High || 0) / totalAnomalies) * 100)
          : 0;

        const mediumPercentage = totalAnomalies
          ? Math.round(((dashboardData.Medium || 0) / totalAnomalies) * 100)
          : 0;

        const lowPercentage = totalAnomalies
          ? Math.round(((dashboardData.Low || 0) / totalAnomalies) * 100)
          : 0;

        setSeverityData([
          {
            name: 'Low Severity',
            value: dashboardData.Low || 0,
            color: '#38bdf8',
            percentage: lowPercentage,
            count: dashboardData.Low || 0
          },
          {
            name: 'Medium Severity',
            value: dashboardData.Medium || 0,
            color: '#f59e0b',
            percentage: mediumPercentage,
            count: dashboardData.Medium || 0
          },
          {
            name: 'High Severity',
            value: dashboardData.High || 0,
            color: '#ef4444',
            percentage: highPercentage,
            count: dashboardData.High || 0
          }
        ]);

        const alertsResponse = await fetch(
          `${baseUrl}/cybedefender/alerts?userId=${userId}&limit=20`,
          {
            method: 'GET',
            headers
          }
        );

        if (alertsResponse.ok) {
          const alertsData = await alertsResponse.json();

          if (alertsData.alerts) {
            const today = new Date().toISOString().split('T')[0];

            const filteredAlerts = alertsData.alerts
              .filter((alert) => {
                if (!alert.date) return true;

                const parsedDate = new Date(alert.date);

                if (Number.isNaN(parsedDate.getTime())) return true;

                return parsedDate.toISOString().split('T')[0] <= today;
              })
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .slice(0, 6);

            setRecentThreats(filteredAlerts);
          }
        }

        const results = await Promise.allSettled([
          fetch(`${baseUrl}/cybedefender/summary?userId=${userId}`, { headers }),
          fetch(`${baseUrl}/cybedefender/incident-summary?userId=${userId}`, { headers }),
          fetch(`${baseUrl}/cybedefender/threat-timeline?userId=${userId}&limit=10`, { headers }),
          fetch(`${baseUrl}/cybedefender/top-threats?userId=${userId}&limit=5`, { headers }),
          fetch(`${baseUrl}/cybedefender/attack-graph?userId=${userId}`, { headers })
        ]);

        const successfulJson = async (result) => {
          if (result.status !== 'fulfilled') return null;
          if (!result.value.ok) return null;

          try {
            return await result.value.json();
          } catch {
            return null;
          }
        };

        const summaryData = await successfulJson(results[0]);
        const incidentData = await successfulJson(results[1]);
        const timelineData = await successfulJson(results[2]);
        const topThreatData = await successfulJson(results[3]);
        const attackGraphData = await successfulJson(results[4]);

        if (summaryData) setSummary(summaryData);
        if (incidentData) setIncidentSummary(incidentData);
        if (timelineData) setTimelineEvents(timelineData.events || []);
        if (topThreatData) setTopThreats(topThreatData.top_threats || []);

        if (attackGraphData) {
          setAttackGraph(attackGraphData || { nodes: [], edges: [] });
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [navigate]);

  useEffect(() => {
    const organizationGuid = activeOrganization?.guid;
    if (!organizationGuid) {
      setCollectorSummary(null);
      return undefined;
    }

    const loadCollectorSummary = async () => {
      const token = localStorage.getItem('token') || Cookies.get('token');
      if (!token) return;
      try {
        const baseUrl = process.env.REACT_APP_CYBEDEFENDER_AI_URL || '/api';
        const query = new URLSearchParams({ organization_guid: organizationGuid });
        const response = await fetch(
          `${baseUrl}/cybedefender/syslog-summary?${query.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          setCollectorSummary(await response.json());
        }
      } catch (summaryError) {
        console.error('Collector summary load failed:', summaryError);
      }
    };

    loadCollectorSummary();
    const timer = setInterval(loadCollectorSummary, 15000);
    return () => clearInterval(timer);
  }, [activeOrganization?.guid]);

  const getSeverityClass = (severity) => {
    switch ((severity || '').toLowerCase()) {
      case 'low':
        return 'bg-sky-50 text-sky-700 ring-sky-200';
      case 'medium':
        return 'bg-amber-50 text-amber-700 ring-amber-200';
      case 'high':
      case 'critical':
        return 'bg-red-50 text-red-700 ring-red-200';
      default:
        return 'bg-slate-50 text-slate-700 ring-slate-200';
    }
  };

  const formatBytes = (bytes) => {
    const value = Number(bytes);

    if (!Number.isFinite(value)) return 'N/A';
    if (value === 0) return '0 B';
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;

    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString();
  };

  const formatTtl = (seconds) => {
    const value = Number(seconds);

    if (!Number.isFinite(value)) return 'N/A';

    if (value % 3600 === 0) {
      const hours = value / 3600;
      return `${hours} hour${hours === 1 ? '' : 's'}`;
    }

    if (value % 60 === 0) {
      return `${value / 60} minutes`;
    }

    return `${value} seconds`;
  };

  const renderCustomizedLabel = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, index } = props;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontWeight="bold"
        fontSize="14"
      >
        {severityData[index]?.percentage || 0}%
      </text>
    );
  };

  if (loading) {
    return (
      <AppLayout onLogout={onLogout}>
                <div className="flex min-h-[50vh] items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-cyan-500" />
            <div className="text-sm font-medium text-slate-600">
              Loading security telemetry...
            </div>
          </div>
        </div>
              </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout onLogout={onLogout}>
                <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
          <AlertTriangle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <h2 className="text-lg font-semibold text-red-900">
            Dashboard data unavailable
          </h2>
          <p className="mt-2 text-sm text-red-700">
            CybeDefender could not load the primary dashboard dataset.
          </p>
        </div>
              </AppLayout>
    );
  }

  // Legacy global syslog status is not customer-owned, so it remains hidden.
  const latestSyslog = null;

  return (
    <AppLayout onLogout={onLogout}>
            <section className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: 'Total Anomalies',
            value: anomalies,
            helper: 'Detected events',
            icon: Activity,
            route: '/alerts'
          },
          {
            label: 'High Severity',
            value: highSeverity,
            helper: 'Priority investigation',
            icon: AlertTriangle,
            route: '/alerts'
          },
          {
            label: 'Medium Severity',
            value: mediumSeverity,
            helper: 'Review recommended',
            icon: Activity,
            route: '/alerts'
          },
          {
            label: 'Low Severity',
            value: lowSeverity,
            helper: 'Lower-priority detections',
            icon: ShieldCheck,
            route: '/alerts'
          }
        ].map((metric) => {
          const Icon = metric.icon;

          return (
            <button
              type="button"
              key={metric.label}
              onClick={() => navigate(metric.route)}
              className="group w-full rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-cyan-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-500">
                    {metric.label}
                  </div>
                  <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
                    {metric.value}
                  </div>
                </div>

                <div className="rounded-xl bg-slate-50 p-2.5 transition-colors group-hover:bg-cyan-50">
                  <Icon className="h-5 w-5 text-slate-600 transition-colors group-hover:text-cyan-700" />
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                <span>{metric.helper}</span>
                <span className="opacity-0 transition-opacity group-hover:opacity-100">
                  View →
                </span>
              </div>
            </button>
          );
        })}
      </section>

      <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Collector telemetry</h3>
            <p className="mt-1 text-sm text-slate-500">Authenticated syslog activity for the active organization.</p>
          </div>
          <button type="button" onClick={() => navigate('/syslog')} className="text-sm font-semibold text-cyan-700 hover:text-cyan-800">
            Open Syslog →
          </button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="rounded-xl bg-slate-50 p-4">
            <div className="text-xs text-slate-400">Events</div>
            <div className="mt-1 text-xl font-semibold text-slate-900">{collectorSummary?.total_events ?? 0}</div>
          </div>
          <div className="rounded-xl bg-slate-50 p-4">
            <div className="text-xs text-slate-400">Open Alerts</div>
            <div className="mt-1 text-xl font-semibold text-slate-900">{collectorSummary?.open_alerts ?? 0}</div>
          </div>
          <div className="rounded-xl bg-slate-50 p-4">
            <div className="text-xs text-slate-400">High Severity</div>
            <div className="mt-1 text-xl font-semibold text-red-600">{collectorSummary?.severity?.high ?? 0}</div>
          </div>
          <div className="rounded-xl bg-slate-50 p-4">
            <div className="text-xs text-slate-400">Latest Event</div>
            <div className="mt-1 text-sm font-semibold text-slate-900">{formatDate(collectorSummary?.latest_event?.timestamp)}</div>
          </div>
        </div>
      </section>

      <section className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Detection Summary
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Customer-scoped detection activity
              </p>
            </div>

            <span className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">
              MONITORING
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-xs text-slate-400">
                Recent Alerts
              </div>
              <div className="mt-1 text-xl font-semibold text-slate-900">
                {recentThreats.length}
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-xs text-slate-400">
                High Severity
              </div>
              <div className="mt-1 font-semibold text-red-600">
                {highSeverity}
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-xs text-slate-400">
                Medium Severity
              </div>
              <div className="mt-1 font-semibold text-amber-600">
                {mediumSeverity}
              </div>
            </div>

            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-xs text-slate-400">
                Low Severity
              </div>
              <div className="mt-1 font-semibold text-sky-600">
                {lowSeverity}
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-xl border border-slate-200 px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-slate-500">
                Total Anomalies
              </span>

              <span className="text-sm font-semibold text-slate-900">
                {anomalies}
              </span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-cyan-50 p-2.5">
                <Sparkles className="h-5 w-5 text-cyan-700" />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  AI Assessment
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Latest AI-assisted analysis
                </p>
              </div>
            </div>

            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                (incidentSummary?.status || '').toLowerCase() === 'clean'
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-slate-100 text-slate-700'
              }`}
            >
              {(incidentSummary?.status || 'Unknown').toUpperCase()}
            </span>
          </div>

          {incidentSummary ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-slate-50 p-3">
                  <div className="text-xs text-slate-400">
                    Severity
                  </div>
                  <div className="mt-1 font-semibold text-slate-900">
                    {incidentSummary.severity || 'N/A'}
                  </div>
                </div>

                <div className="rounded-xl bg-slate-50 p-3">
                  <div className="text-xs text-slate-400">
                    Risk Score
                  </div>
                  <div className="mt-1 font-semibold text-slate-900">
                    {incidentSummary.risk_score ?? 'N/A'}
                  </div>
                </div>

                <div className="rounded-xl bg-slate-50 p-3">
                  <div className="text-xs text-slate-400">
                    Top Threat
                  </div>
                  <div className="mt-1 truncate font-semibold text-slate-900">
                    {incidentSummary.top_threat || 'N/A'}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Summary
                </div>
                <p className="mt-1 text-sm leading-5 text-slate-600">
                  {incidentSummary.summary || 'No assessment summary available.'}
                </p>
              </div>

              <div className="mt-3">
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Recommended Action
                </div>
                <p className="mt-1 text-sm leading-5 text-slate-600">
                  {incidentSummary.recommended_action || 'N/A'}
                </p>
              </div>
            </>
          ) : (
            <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
              No AI assessment is currently available.
            </div>
          )}
        </div>
      </section>

      <section className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm xl:col-span-2">
          <div className="mb-5">
            <h3 className="text-lg font-semibold text-slate-900">
              Monthly Threat Activity
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Detection activity over time
            </p>
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyData}>
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#0891b2"
                  strokeWidth={3}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">
            Security Posture
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Severity distribution
          </p>

          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={severityData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={95}
                  label={renderCustomizedLabel}
                  labelLine={false}
                >
                  {severityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-lg font-semibold text-red-600">
                {highSeverity}
              </div>
              <div className="text-xs text-slate-400">High</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-amber-600">
                {mediumSeverity}
              </div>
              <div className="text-xs text-slate-400">Medium</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-sky-600">
                {lowSeverity}
              </div>
              <div className="text-xs text-slate-400">Low</div>
            </div>
          </div>
        </div>
      </section>

      <section className="hidden mb-8 grid grid-cols-1 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded-xl bg-cyan-50 p-2.5">
              <Sparkles className="h-5 w-5 text-cyan-700" />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                AI Incident Summary
              </h3>
              <p className="text-sm text-slate-500">
                Latest AI-assisted assessment
              </p>
            </div>
          </div>

          {incidentSummary ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-xs text-slate-400">Status</div>
                  <div className="mt-1 font-semibold text-slate-800">
                    {incidentSummary.status || 'N/A'}
                  </div>
                </div>

                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-xs text-slate-400">Severity</div>
                  <div className="mt-1 font-semibold text-slate-800">
                    {incidentSummary.severity || 'N/A'}
                  </div>
                </div>

                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-xs text-slate-400">Confidence</div>
                  <div className="mt-1 font-semibold text-slate-800">
                    {incidentSummary.confidence ?? 'N/A'}
                  </div>
                </div>

                <div className="rounded-xl bg-slate-50 p-2.5">
                  <div className="text-xs text-slate-400">Risk Score</div>
                  <div className="mt-1 font-semibold text-slate-800">
                    {incidentSummary.risk_score ?? 'N/A'}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Top Threat
                </div>
                <div className="mt-1 font-semibold text-slate-900">
                  {incidentSummary.top_threat || 'N/A'}
                </div>
              </div>

              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Summary
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  {incidentSummary.summary || 'No AI summary available.'}
                </p>
              </div>

              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Recommended Action
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  {incidentSummary.recommended_action || 'N/A'}
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-slate-50 p-6 text-sm text-slate-500">
              No incident summary is currently available.
            </div>
          )}
        </div>

        <div className="hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-center gap-3">
            <div className="rounded-xl bg-violet-50 p-2.5">
              <FileUp className="h-5 w-5 text-violet-700" />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Recent Upload Activity
              </h3>
              <p className="text-sm text-slate-500">
                Latest ingestion audit records
              </p>
            </div>
          </div>

          {uploadAudit.length ? (
            <div className="space-y-3">
              {uploadAudit.slice(0, 5).map((upload, index) => (
                <div
                  key={`${upload.job_id || 'upload'}-${index}`}
                  className="rounded-xl border border-slate-200 p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-900">
                        {upload.filename || 'Unnamed upload'}
                      </div>

                      <div className="mt-1 text-xs text-slate-500">
                        {formatDate(upload.ts)}
                      </div>
                    </div>

                    <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                      {upload.status || 'Unknown'}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                    <span>{formatBytes(upload.size)}</span>
                    <span>Job {upload.job_id ?? 'N/A'}</span>
                    <span>{upload.reason || 'N/A'}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl bg-slate-50 p-6 text-sm text-slate-500">
              No upload audit activity is available.
            </div>
          )}
        </div>
      </section>

      <section className="mb-8 rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-5">
          <h3 className="text-lg font-semibold text-slate-900">
            Recent Alerts
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Most recent threat detections
          </p>
        </div>

        {recentThreats.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/70 text-left text-xs font-medium uppercase tracking-wide text-slate-400">
                  <th className="px-6 py-3">Threat</th>
                  <th className="px-6 py-3">Severity</th>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Status</th>
                </tr>
              </thead>

              <tbody>
                {recentThreats.map((threat, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-slate-100 last:border-0"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                      {threat.threatType || 'Unknown threat'}
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${getSeverityClass(
                          threat.severity
                        )}`}
                      >
                        {threat.severity || 'Unknown'}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-sm text-slate-500">
                      {threat.date || 'N/A'}
                    </td>

                    <td className="px-6 py-4 text-sm text-slate-500">
                      {threat.status || 'Detected'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-sm text-slate-500">
            No recent alerts available.
          </div>
        )}
      </section>

      <section className="hidden mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">
            Top Threats
          </h3>

          <div className="mt-5">
            {topThreats.length ? (
              <div className="space-y-3">
                {topThreats.map((threat, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3"
                  >
                    <span className="text-sm font-medium text-slate-700">
                      {threat.threatType}
                    </span>

                    <span className="text-sm font-semibold text-slate-900">
                      {threat.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500">
                No top threat data available.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900">
            Threat Timeline
          </h3>

          <div className="mt-5">
            {timelineEvents.length ? (
              <div className="max-h-80 space-y-4 overflow-y-auto">
                {timelineEvents.map((event, idx) => (
                  <div
                    key={idx}
                    className="flex gap-3 border-b border-slate-100 pb-4 last:border-0"
                  >
                    <div className="mt-1">
                      <div className="h-2.5 w-2.5 rounded-full bg-cyan-500" />
                    </div>

                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800">
                        {event.threatType}
                      </div>

                      <div className="mt-1 text-xs text-slate-400">
                        {event.time}
                      </div>

                      <div className="mt-2 flex items-center gap-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs ring-1 ring-inset ${getSeverityClass(
                            event.severity
                          )}`}
                        >
                          {event.severity}
                        </span>

                        <span className="text-xs text-slate-500">
                          Count: {event.count}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500">
                No timeline data available.
              </div>
            )}
          </div>
        </div>
      </section>

      {latestSyslog && (
        <section className="hidden mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-center gap-3">
            <div className="rounded-xl bg-orange-50 p-2.5">
              <Radio className="h-5 w-5 text-orange-700" />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Latest Syslog Detection
              </h3>
              <p className="text-sm text-slate-500">
                Most recent enriched syslog alert
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <div className="text-xs text-slate-400">Threat Type</div>
              <div className="mt-1 font-semibold text-slate-800">
                {latestSyslog.threat_type || 'N/A'}
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-400">Source IP</div>
              <div className="mt-1 font-mono text-sm text-slate-800">
                {latestSyslog.client_ip || 'N/A'}
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-400">Severity</div>
              <div className="mt-1 font-semibold text-slate-800">
                {latestSyslog.severity || 'N/A'}
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-400">Threat Score</div>
              <div className="mt-1 font-semibold text-slate-800">
                {latestSyslog.threat_score ?? 'N/A'}
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-400">ASN</div>
              <div className="mt-1 text-sm text-slate-700">
                {latestSyslog.asn || 'N/A'}
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-400">Organization</div>
              <div className="mt-1 text-sm text-slate-700">
                {latestSyslog.asn_org || latestSyslog.isp || 'N/A'}
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-400">Country</div>
              <div className="mt-1 text-sm text-slate-700">
                {latestSyslog.country || 'N/A'}
              </div>
            </div>

            <div>
              <div className="text-xs text-slate-400">Created</div>
              <div className="mt-1 text-sm text-slate-700">
                {formatDate(latestSyslog.created_at)}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="hidden mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900">
          Attack Chain Analytics
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          Observed relationships across detected attack activity
        </p>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <h4 className="mb-3 text-sm font-semibold text-slate-700">
              Nodes
            </h4>

            {attackGraph.nodes?.length ? (
              <div className="space-y-2">
                {attackGraph.nodes.map((node, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between rounded-xl bg-slate-50 px-4 py-3"
                  >
                    <span className="text-sm text-slate-700">{node.id}</span>
                    <span className="text-sm font-semibold text-slate-900">
                      {node.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500">
                No attack nodes available.
              </div>
            )}
          </div>

          <div>
            <h4 className="mb-3 text-sm font-semibold text-slate-700">
              Edges
            </h4>

            {attackGraph.edges?.length ? (
              <div className="space-y-2">
                {attackGraph.edges.map((edge, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between rounded-xl bg-slate-50 px-4 py-3"
                  >
                    <span className="text-sm text-slate-700">
                      {edge.source} → {edge.target}
                    </span>
                    <span className="text-sm font-semibold text-slate-900">
                      {edge.count}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500">
                No attack edges available yet.
              </div>
            )}
          </div>
        </div>
      </section>

      {summary && (
        <section className="hidden mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-center gap-3">
            <Database className="h-5 w-5 text-cyan-700" />
            <h3 className="text-lg font-semibold text-slate-900">
              Latest Analysis
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              ['File', summary.file],
              ['Rows Processed', summary.rows_processed],
              ['Threats Detected', summary.threats_detected],
              ['Avg Confidence', summary.avg_confidence ?? 'N/A'],
              ['Risk Score', summary.risk_score ?? 'N/A'],
              ['Top Threat', summary.top_threat || 'N/A'],
              ['Normal %', summary.normal_percent],
              [
                'Processing Time',
                summary.processing_time !== undefined
                  ? `${summary.processing_time}s`
                  : 'N/A'
              ]
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl bg-slate-50 p-4">
                <div className="text-xs text-slate-400">{label}</div>
                <div className="mt-1 truncate font-semibold text-slate-800">
                  {value}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="hidden mb-8 grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-500">
            <Shield className="h-4 w-4" />
            Hard Infrastructure Protection
          </div>
          <div className="mt-3 text-lg font-semibold text-slate-900">
            {policy
              ? policy.hard_protected_infra_skip
                ? 'Enabled'
                : 'Disabled'
              : 'Unknown'}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-500">
            <ShieldCheck className="h-4 w-4" />
            Soft Infrastructure Protection
          </div>
          <div className="mt-3 text-lg font-semibold text-slate-900">
            {policy
              ? policy.soft_protected_infra_skip
                ? 'Enabled'
                : 'Disabled'
              : 'Unknown'}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-500">
            <Clock3 className="h-4 w-4" />
            Default Enforcement TTL
          </div>
          <div className="mt-3 text-lg font-semibold text-slate-900">
            {policy
              ? formatTtl(policy.default_block_ttl_seconds)
              : 'Unknown'}
          </div>
        </div>
      </section>

          </AppLayout>
  );
};

export default Dashboard;
